import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Users, MapPin, User, FileText, Pencil, PowerOff } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import Modal from '../../components/ui/Modal';
import { useClient } from '../../context/ClientContext';
import { useAuth } from '../../context/AuthContext';

const emptyForm = {
  name: '', code: '',
  contactPerson: '', phone: '', email: '', gstNumber: '',
  address: { street: '', city: '', state: '', pincode: '' },
};

const ClientCard = ({ c, onEdit, onToggle, onActivate, canManage }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    className={`zip-card p-5 flex flex-col gap-4 transition-opacity ${c.isActive ? '' : 'opacity-60'}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-zip-red/10 border border-zip-red/20 flex items-center justify-center flex-shrink-0">
          <Users size={18} className="text-zip-red" />
        </div>
        <div>
          <div className="font-semibold text-zip-text text-sm leading-tight">{c.name}</div>
          <div className="text-[11px] font-mono text-zip-muted mt-0.5">{c.code}</div>
        </div>
      </div>
      <span className={c.isActive ? 'chip-green' : 'chip-gray'}>{c.isActive ? 'Active' : 'Inactive'}</span>
    </div>

    <div className="space-y-1.5 text-xs text-zip-muted">
      {c.contactPerson && (
        <div className="flex items-center gap-2">
          <User size={12} className="flex-shrink-0 text-zip-red/70" />
          <span>{c.contactPerson}</span>
          {c.phone && <span className="font-mono">· {c.phone}</span>}
        </div>
      )}
      {(c.address?.city || c.address?.state) && (
        <div className="flex items-start gap-2">
          <MapPin size={12} className="mt-0.5 flex-shrink-0 text-zip-red/70" />
          <span>{[c.address?.city, c.address?.state, c.address?.pincode].filter(Boolean).join(', ')}</span>
        </div>
      )}
      {c.gstNumber && (
        <div className="flex items-center gap-2">
          <FileText size={12} className="flex-shrink-0 text-zip-red/70" />
          <span className="font-mono">{c.gstNumber}</span>
        </div>
      )}
    </div>

    {canManage && (
      <div className="flex items-center gap-2 pt-1 border-t border-zip-border">
        <button onClick={() => onEdit(c)} className="flex items-center gap-1.5 text-xs font-medium text-zip-muted hover:text-zip-text transition-colors px-2 py-1 rounded hover:bg-zip-hover">
          <Pencil size={12} /> Edit
        </button>
        {c.isActive ? (
          <button
            onClick={() => onToggle(c)}
            className="flex items-center gap-1.5 text-xs font-medium transition-colors px-2 py-1 rounded text-zip-muted hover:text-zip-red hover:bg-red-50"
          >
            <PowerOff size={12} /> Deactivate
          </button>
        ) : (
          <button
            onClick={() => onActivate(c)}
            className="flex items-center gap-1.5 text-xs font-medium transition-colors px-2 py-1 rounded text-emerald-600 hover:bg-emerald-50"
          >
            <PowerOff size={12} /> Enable
          </button>
        )}
      </div>
    )}
  </motion.div>
);

const ClientList = () => {
  const { clients, refetchClients } = useClient();
  const { can } = useAuth();
  const canManage = can('clients.manage');
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [inactiveClients, setInactiveClients] = useState(null);

  const fetchWithInactive = useCallback(async () => {
    try {
      const res = await api.get('/clients?includeInactive=true');
      setInactiveClients(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load clients');
    }
  }, []);

  useEffect(() => {
    if (showInactive) fetchWithInactive();
  }, [showInactive, fetchWithInactive]);

  const displayed = showInactive ? (inactiveClients || []) : clients;

  const openCreate = () => { setEditTarget(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (c) => {
    setEditTarget(c);
    setForm({
      name: c.name, code: c.code,
      contactPerson: c.contactPerson || '', phone: c.phone || '',
      email: c.email || '', gstNumber: c.gstNumber || '',
      address: { street: '', city: '', state: '', pincode: '', ...c.address },
    });
    setShowForm(true);
  };

  const handleToggle = async (c) => {
    try {
      await api.put(`/clients/${c._id}`, { isActive: !c.isActive });
      toast.success(`Client ${c.isActive ? 'deactivated' : 'activated'}`);
      refetchClients();
      if (showInactive) fetchWithInactive();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update client');
    }
  };

  const handleActivate = async (c) => {
    try {
      await api.patch(`/clients/${c._id}/activate`);
      toast.success('Client re-enabled');
      refetchClients();
      if (showInactive) fetchWithInactive();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to activate client');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editTarget) {
        await api.put(`/clients/${editTarget._id}`, form);
        toast.success('Client updated');
      } else {
        await api.post('/clients', form);
        toast.success('Client created');
      }
      setShowForm(false);
      refetchClients();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save client');
    } finally {
      setSubmitting(false);
    }
  };

  const setAddr = (key, val) => setForm((f) => ({ ...f, address: { ...f.address, [key]: val } }));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <p className="text-sm text-zip-muted">Companies whose goods you store &amp; handle.</p>
          <label className="flex items-center gap-2 text-sm text-zip-muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="accent-zip-red"
            />
            Show inactive
          </label>
        </div>
        {canManage && (
          <button onClick={openCreate} className="btn-primary flex items-center gap-2">
            <Plus size={15} /> Add Client
          </button>
        )}
      </div>

      {displayed.length === 0 ? (
        <div className="zip-card p-10 text-center text-zip-muted text-sm">
          {showInactive
            ? 'No inactive clients found.'
            : <>No clients yet. Click <strong>Add Client</strong> to onboard your first client.</>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {displayed.map((c) => (
            <ClientCard key={c._id} c={c} onEdit={openEdit} onToggle={handleToggle} onActivate={handleActivate} canManage={canManage} />
          ))}
        </div>
      )}

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editTarget ? 'Edit Client' : 'Add Client'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Company Name *</label>
              <input className="zip-input" required placeholder="Acme Traders Pvt Ltd"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Code *</label>
              <input className="zip-input uppercase" required placeholder="CL01" maxLength={10}
                value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                disabled={!!editTarget} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Contact Person</label>
              <input className="zip-input" placeholder="Rahul Mehta"
                value={form.contactPerson} onChange={(e) => setForm({ ...form, contactPerson: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Phone</label>
              <input className="zip-input font-mono" placeholder="9876543210"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Email</label>
              <input type="email" className="zip-input" placeholder="ops@acme.com"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">GST Number</label>
              <input className="zip-input font-mono uppercase" placeholder="24ABCDE1234F1Z5"
                value={form.gstNumber} onChange={(e) => setForm({ ...form, gstNumber: e.target.value.toUpperCase() })} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Street Address</label>
            <input className="zip-input" placeholder="Plot 14, GIDC"
              value={form.address.street} onChange={(e) => setAddr('street', e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">City</label>
              <input className="zip-input" placeholder="Vadodara"
                value={form.address.city} onChange={(e) => setAddr('city', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">State</label>
              <input className="zip-input" placeholder="Gujarat"
                value={form.address.state} onChange={(e) => setAddr('state', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">Pincode</label>
              <input className="zip-input font-mono" placeholder="390010"
                value={form.address.pincode} onChange={(e) => setAddr('pincode', e.target.value)} />
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={submitting} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {submitting
                ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving…</>
                : editTarget ? 'Save Changes' : 'Create Client'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ClientList;
